import { clsx } from 'clsx';

interface OptionToggleProps {
  selected: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  /**
   * `card` (défaut) : option autonome, encadrée — formulaires, sélections de comptes.
   * `row` : liste dense sans bordure — dropdowns, panneaux de filtre, longues listes.
   * `box` : pastille carrée compacte — cellules de tableau, où une option pleine
   *   largeur ne rentre pas. `children` sert alors de libellé accessible.
   */
  variant?: 'card' | 'row' | 'box';
  /** Pastille de couleur affichée avant le libellé. */
  color?: string;
  /**
   * Icône d'en-tête, rendue dans une tuile teintée par `color`. Donne aux
   * comptes bancaires une identité distincte des options génériques (bascule
   * automatiquement la mise en page en `account`).
   */
  icon?: React.ReactNode;
  /** Sous-libellé secondaire (solde, description courte) — mise en page `account`. */
  sublabel?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  title?: string;
}

/**
 * Option sélectionnable — remplaçant unique des `<input type="checkbox">`.
 *
 * Mécanique commune à tout le site : **la surbrillance EST l'état**. Pas de
 * coche ni de carré — le fond teinté à l'accent et la bordure accentuée
 * suffisent à distinguer sélectionné de non sélectionné, sans le rendu natif
 * des cases (non stylable de façon fiable, dépendant de l'OS).
 *
 * C'est un vrai `<button aria-pressed>` : l'état est porté par l'ARIA, donc
 * correctement annoncé aux lecteurs d'écran et activable au clavier
 * (Entrée / Espace) — la couleur n'est jamais le seul véhicule de l'information.
 */
export default function OptionToggle({
  selected,
  onToggle,
  children,
  variant = 'card',
  color,
  icon,
  sublabel,
  disabled = false,
  className,
  title,
}: OptionToggleProps) {
  const isAccount = !!icon;
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={selected}
      title={title}
      style={color ? ({ '--opt-color': color } as React.CSSProperties) : undefined}
      className={clsx(
        'opt',
        variant === 'row' && 'opt--row',
        variant === 'box' && 'opt--box',
        isAccount && 'opt--account',
        selected && 'on',
        disabled && 'opt--disabled',
        className,
      )}
    >
      {icon ? (
        <span className="opt-ic">{icon}</span>
      ) : (
        color && <span className="opt-dot" style={{ backgroundColor: color }} />
      )}
      {/* En variante `box`, le libellé n'est pas peint mais reste lisible par
          les lecteurs d'écran : la pastille seule serait muette. */}
      <span className={variant === 'box' ? 'sr-only' : 'opt-body'}>
        {children}
        {sublabel && <span className="text-xs" style={{ color: 'var(--text-3)' }}>{sublabel}</span>}
      </span>
    </button>
  );
}
